import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { ColDef } from 'ag-grid-community';
import { useCallback } from 'react';
import { Module } from 'src/domain';
import { FormattedAuditDetails } from 'src/domain/models/AuditDetailsModel';
import { auditLogDetailsVar, auditLogDrawerOpenVar } from 'src/infrastructure/local_state';
import { formatAuditLogChange } from 'src/utils/audit-log/formatters/change-formatter';
import { useInventoryMasterDataQuery } from '../inventory-master-data/queries/useInventoryMasterDataQuery';
import { usePhaseoutProfiles } from '../style-timeline/usePhaseoutProfiles';
import { useAuditLogDetailsApiQuery } from './queries/useAuditLogDetailsApiQuery';
import { useAuditLogModuleType } from './useAuditLogModuleType';

interface ShowAuditLogDetailsProps<TData> {
    data: TData;
    colDefs: ColDef<TData>[];
}

type ShowAuditLogDetailsFn<TData> = (props: ShowAuditLogDetailsProps<TData>) => void;

// biome-ignore lint/suspicious/noExplicitAny: TData can be of any type, but must have an id field
export function useAuditLogDetails<TData extends { id: number } = any>(module?: Module) {
    const auditLogModule = useAuditLogModuleType(module, true);
    const [_drawerOpen, setDrawerOpen] = useReactiveVar(auditLogDrawerOpenVar);
    const [_details, setDetails] = useReactiveVar(auditLogDetailsVar);

    const fetchAuditLogDetails = useAuditLogDetailsApiQuery();
    const { data: chainFurnitures } = useInventoryMasterDataQuery();
    const { data: phaseoutProfiles } = usePhaseoutProfiles();

    return useCallback<ShowAuditLogDetailsFn<TData>>(
        async ({ data, colDefs }) => {
            try {
                setDrawerOpen(true);
                setDetails({ loading: true, error: null, details: new Map() });
                const entityId = data.id;

                if (entityId === null || entityId === undefined) {
                    // No entity ID, cannot fetch details. Clear details, but keep drawer open.
                    // For sales campaign details this makes a lot of sense as new rows won't have an ID yet.
                    // For other modules the backend will return an empty list, because all rows have an ID.
                    setDetails({
                        loading: false,
                        error: null,
                        details: new Map(),
                    });
                    return;
                }

                const details = await fetchAuditLogDetails(entityId, auditLogModule);
                const formattedDetails = details.reduce((acc, detail) => {
                    const formattedDetail = acc.get(detail.date) ?? new Map();
                    const changes: FormattedAuditDetails[] = formattedDetail.get(detail.userName) ?? [];
                    const formattedValue = formatAuditLogChange(detail, details, colDefs, auditLogModule, {
                        chainFurnitures,
                        phaseoutProfiles,
                    });
                    if (formattedValue) {
                        changes.push({
                            ...detail,
                            fieldName: detail.fieldName === 'ExitWeekType' ? 'ExitWeek' : detail.fieldName,
                            formattedOldValue: formattedValue.oldValue,
                            formattedNewValue: formattedValue.newValue,
                        });
                    }
                    formattedDetail.set(detail.userName, changes);
                    acc.set(detail.date, formattedDetail);
                    return acc;
                }, new Map<string, Map<string, FormattedAuditDetails[]>>());

                setDetails({ loading: false, error: null, details: formattedDetails });
            } catch (err) {
                setDetails({ loading: false, error: err as Error, details: new Map() });
            } finally {
                setDetails((prev) => ({ ...prev, loading: false }));
            }
        },
        [auditLogModule, fetchAuditLogDetails, setDrawerOpen, setDetails, chainFurnitures, phaseoutProfiles]
    );
}
